import { Component } from '@theme/component';

/**
 * Product info tabs — a slider/read-more tab switcher (not a stacked accordion).
 *
 * Each `_product-info-tab` child block renders itself in full (button + panel,
 * required so its own settings are readable via `content_for 'blocks'`). On
 * connect, this component moves each button into the nav row and each panel
 * into the panels container, then drives which panel is visible. Without JS,
 * every tab's title + content stays visible, stacked and readable.
 *
 * @typedef {object} ProductInfoTabsRefs
 * @property {HTMLElement} nav
 * @property {HTMLElement} indicator
 * @property {HTMLElement} panelsWrap
 * @property {HTMLElement[]} tabItems
 * @property {HTMLButtonElement[]} tabButtons
 * @property {HTMLElement[]} tabPanels
 *
 * @extends {Component<ProductInfoTabsRefs>}
 */
export class ProductInfoTabsComponent extends Component {
  requiredRefs = ['nav', 'indicator', 'panelsWrap', 'tabItems', 'tabButtons', 'tabPanels'];

  connectedCallback() {
    super.connectedCallback();
    this.#hydrate();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  #hydrate() {
    const { nav, panelsWrap, tabItems, tabButtons, tabPanels } = this.refs;

    if (!tabButtons.length || !tabPanels.length) return;

    tabButtons.forEach((button, index) => {
      nav.appendChild(button);
      button.addEventListener('click', () => this.selectTab(index));
    });

    tabPanels.forEach((panel) => panelsWrap.appendChild(panel));
    tabItems.forEach((item) => item.remove());

    nav.addEventListener('keydown', (event) => this.#handleKeyDown(event));

    this.classList.add('is-hydrated');

    this.activeIndex = 0;
    this.selectTab(0);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.#positionIndicator());
      this.resizeObserver.observe(this);
    }
  }

  /**
   * Activates the tab at the given index.
   * @param {number} index
   */
  selectTab(index) {
    const { tabButtons, tabPanels } = this.refs;
    const button = tabButtons[index];
    const panel = tabPanels[index];
    if (!button || !panel) return;

    this.activeIndex = index;

    tabButtons.forEach((tabButton, i) => {
      const selected = i === index;
      tabButton.setAttribute('aria-selected', String(selected));
      tabButton.tabIndex = selected ? 0 : -1;
    });

    tabPanels.forEach((tabPanel, i) => {
      tabPanel.classList.toggle('is-active', i === index);
    });

    this.#positionIndicator();
  }

  /**
   * Arrow/Home/End keyboard navigation on the tablist (WAI-ARIA tabs pattern,
   * automatic activation: moving focus also switches the panel).
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    const { tabButtons } = this.refs;
    const count = tabButtons.length;
    if (!count) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (this.activeIndex + 1) % count;
        break;
      case 'ArrowLeft':
        nextIndex = (this.activeIndex - 1 + count) % count;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(nextIndex);
    tabButtons[nextIndex]?.focus();
  }

  #positionIndicator() {
    const { tabButtons, indicator } = this.refs;
    const button = tabButtons[this.activeIndex];
    if (!button) return;

    indicator.style.setProperty('--tabs-indicator-width', `${button.offsetWidth}px`);
    indicator.style.setProperty('--tabs-indicator-offset', `${button.offsetLeft}px`);
    indicator.classList.add('is-visible');
  }
}

if (!customElements.get('product-info-tabs-component')) {
  customElements.define('product-info-tabs-component', ProductInfoTabsComponent);
}
